import {
  BusEvent,
  CharacteristicType,
  FormProcessInputType,
  FormProcessType,
  HeatingCalculationMethod,
  HpStep,
  InstallationType,
  KeyNumType,
  ProductStatus,
  ProductType,
  ProjectPropertyKeyType,
  ProjectPropertyType,
  ProjectPropertyValueType,
  ProjectStatus,
  ReportVariant,
  SalesPackageType,
  ValidationRuleType
} from '@/helper/Types'
import { ProductResponse } from '@/services/models/ProductResponse'
import userStore from '@/store/UserStore'
import { SchemeResponse } from '@/services/models/SchemeResponse'
import bus from '@/helper/Bus'
import { Characteristic } from '@/services/models/Characteristic'
import { HPCharacteristic } from '@/services/models/HPCharacteristic'
import { Heatpump } from '@/services/models/implementations/Heatpump'
import { DhwStorage } from '@/services/models/implementations/DhwStorage'
import { BackupHeater } from '@/services/models/implementations/BackupHeater'
import { reactive } from 'vue'
import { containsRuleRequired } from '@/helper/ValidationRules'
import ProjectController from '@/controllers/ProjectController'
import { BackupHeaterCharacteristic } from '@/services/models/BackupHeaterCharacteristic'
import { BufferTankSuggestion } from '@/services/models/BufferTankSuggestion'
import { Buffertank } from '@/services/models/implementations/Buffertank'
import { Accessory } from '@/services/models/implementations/Accessory.ts'
import { Article } from '@/services/models/Article.ts'
import { ProjectOrigin } from '@/models/ProjectOrigin.ts'

class ProjectStore {
  private componentName = 'projectStore'
  private _projectId = ''
  private _lastModified = ''
  private _salesPackages?: Article[]
  private _totalPrice?: number
  private _status?: ProjectStatus
  private _errorLoadingProject = false
  private _state: Record<string, FormProcessInputType>[] = []
  private phasedOutSteps: HpStep[] = []
  private _reportVariants: ReportVariant[] = [ReportVariant.INSTALLER, ReportVariant.CONSUMER]
  private _showPrices = false
  private _showPricesCustomer = false
  private _showExtendedNoiseValues = false
  private _origin: ProjectOrigin = ProjectOrigin.STANDALONE

  private _resolvedAccessories?: ProductResponse[] = []

  private _products: Record<string, ProductResponse[] | undefined> = {
    [ProductType.BACKUPHEATER]: [],
    [ProductType.BUFFERTANK]: [],
    [ProductType.DHWTANK]: [],
    [ProductType.HEATPUMP]: [],
    [ProductType.SYSTEM_CONTROLLER]: [],
    [ProductType.SALES_PACKAGE]: []
  }

  private _selectedProducts: { [key: string]: ProductResponse | undefined } = {
    [ProductType.BACKUPHEATER]: undefined,
    [ProductType.ADDITIONAL_PRODUCT]: undefined,
    [ProductType.BUFFERTANK]: undefined,
    [ProductType.COOLING_BUFFERTANK]: undefined,
    [ProductType.DHWTANK]: undefined,
    [ProductType.HEATPUMP]: undefined,
    [ProductType.SYSTEM_CONTROLLER]: undefined,
    [ProductType.SALES_PACKAGE]: undefined
  }

  private _selectedArticles: Article[] = []

  // prevent reset of these properties
  private _accessories: Accessory[] | undefined = []
  private _preselectedAccessories: KeyNumType = {}
  private _systemSchemes: SchemeResponse[] | undefined = []

  private _projectProperties: ProjectPropertyType = {
    name: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PROJECT_DETAILS,
      resetsFromStep: HpStep.EMPTY,
      required: true,
      formValidationRule: 'required|max:200',
      productDependency: undefined
    },
    firstName: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PROJECT_DETAILS,
      resetsFromStep: HpStep.EMPTY,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    lastName: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PROJECT_DETAILS,
      resetsFromStep: HpStep.EMPTY,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    phoneNumber: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PROJECT_DETAILS,
      resetsFromStep: HpStep.EMPTY,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    emailAddress: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PROJECT_DETAILS,
      resetsFromStep: HpStep.EMPTY,
      required: false,
      formValidationRule: 'email',
      productDependency: undefined
    },
    customerStreet: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PROJECT_DETAILS,
      resetsFromStep: HpStep.EMPTY,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    customerHouseNumber: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PROJECT_DETAILS,
      resetsFromStep: HpStep.EMPTY,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    customerPostalCode: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PROJECT_DETAILS,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: false,
      formValidationRule: (): Record<string, unknown> => {
        return {
          validPostalCode: {
            availableCodes: () => (userStore.validCustomerPostalCodes ? userStore.validCustomerPostalCodes : []),
            isActive: userStore.enableCityDropdown,
            regexp: () => userStore.postalCodeValidationPattern
          }
        }
      },
      productDependency: undefined
    },
    customerCity: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PROJECT_DETAILS,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    projectStreet: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PROJECT_DETAILS,
      resetsFromStep: HpStep.EMPTY,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    projectHouseNumber: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PROJECT_DETAILS,
      resetsFromStep: HpStep.EMPTY,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    projectPostalCode: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PROJECT_DETAILS,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: true,
      formValidationRule: (): Record<string, unknown> => {
        return {
          required: true,
          validPostalCode: {
            availableCodes: () => (userStore.validProjectPostalCodes ? userStore.validProjectPostalCodes : []),
            isActive: userStore.enableCityDropdown,
            regexp: () => userStore.postalCodeValidationPattern
          }
        }
      },
      productDependency: undefined
    },
    projectCity: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PROJECT_DETAILS,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: true,
      formValidationRule: 'required',
      productDependency: undefined
    },
    projectAltitude: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.INT,
      step: HpStep.PROJECT_DETAILS,
      resetsFromStep: HpStep.EMPTY,
      formValidationRule: 'integer|min_value:0|required',
      required: () => userStore.enableAltitudeInput,
      productDependency: undefined
    },
    heatingTemplateId: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.INT,
      step: HpStep.EMPTY,
      resetsFromStep: HpStep.EMPTY,
      formValidationRule: 'integer|min_value:0',
      required: false,
      productDependency: undefined
    },
    coolingTemplateId: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.INT,
      step: HpStep.EMPTY,
      resetsFromStep: HpStep.EMPTY,
      formValidationRule: 'integer|min_value:0',
      required: false,
      productDependency: undefined
    },
    heatingLoss: {
      val: undefined,
      formProcessType: FormProcessType.INTL_NUMBER,
      formRevertType: FormProcessType.INTL_NUMBER,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isHeating
        && !this.isHeatingCalculatedByConsumption
        && !this.isHeatingCalculatedByThermalEnvelope,
      formValidationRule: 'required|positiveDecimal',
      productDependency: undefined
    },
    livingArea: {
      val: undefined,
      formProcessType: FormProcessType.INTL_NUMBER,
      formRevertType: FormProcessType.INTL_NUMBER,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => userStore.enableLivingArea
        && this.isHeating
        && !this.isHeatingCalculatedByConsumption
        && !this.isHeatingCalculatedByThermalEnvelope,
      formValidationRule: 'required|integer|min_value:1',
      productDependency: undefined
    },
    coolingLoad: {
      val: undefined,
      formProcessType: FormProcessType.INTL_NUMBER,
      formRevertType: FormProcessType.INTL_NUMBER,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isCooling && !this.isCoolingCalculated,
      formValidationRule: 'required|positiveDecimal',
      productDependency: undefined
    },
    heatingCalculationMethod: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    coolingCalculationMethod: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    constructionYear: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isCoolingCalculated || this.isHeatingCalculatedByThermalEnvelope,
      formValidationRule: 'required',
      productDependency: undefined
    },
    coolingIndoorTemperature: {
      val: undefined,
      formProcessType: FormProcessType.INT,
      formRevertType: FormProcessType.INT,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isCoolingCalculated,
      formValidationRule: 'required',
      productDependency: undefined
    },
    heatingIndoorTemperature: {
      val: undefined,
      formProcessType: FormProcessType.INT,
      formRevertType: FormProcessType.INT,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isHeatingCalculatedByThermalEnvelope,
      formValidationRule: 'required',
      productDependency: undefined
    },
    previousFuelType: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isHeatingCalculatedByConsumption,
      formValidationRule: 'required',
      productDependency: undefined
    },
    operatingHours: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.DECIMAL,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isHeatingCalculatedByConsumption,
      formValidationRule: 'required|positiveDecimal',
      productDependency: undefined
    },
    annualConsumption: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.DECIMAL,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isHeatingCalculatedByConsumption,
      formValidationRule: 'required|positiveDecimal',
      productDependency: undefined
    },
    annualConsumptionUnit: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isHeatingCalculatedByConsumption,
      formValidationRule: 'required',
      productDependency: undefined
    },
    buildingHull: {
      val: undefined,
      formProcessType: FormProcessType.BUILDING_HULL,
      formRevertType: FormProcessType.BUILDING_HULL,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isCoolingCalculated || this.isHeatingCalculatedByThermalEnvelope,
      formValidationRule: {
        stories: {
          livingArea: 'required|integer|min_value:1',
          ceilingHeight: 'required|positiveDecimal|max_value:30',
          length: 'required|positiveDecimal|max_value:30',
          width: 'required|positiveDecimal|max_value:30',
        },
      },
      productDependency: undefined
    },
    glazedSurfaces: {
      val: undefined,
      formProcessType: FormProcessType.GLAZED_SURFACE,
      formRevertType: FormProcessType.GLAZED_SURFACE,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isCoolingCalculated,
      formValidationRule: {
        name: 'required',
        orientation: 'required',
        width: 'required|positiveDecimal|max_value:20',
        height: 'required|positiveDecimal|max_value:20'
      },
      productDependency: undefined
    },
    coolingVentilationType: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isCoolingCalculated,
      formValidationRule: 'required',
      productDependency: undefined
    },
    heatingVentilationType: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isHeatingCalculatedByThermalEnvelope,
      formValidationRule: 'required',
      productDependency: undefined
    },
    windowType: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isHeatingCalculatedByThermalEnvelope,
      formValidationRule: 'required',
      productDependency: undefined
    },
    adjoinedBuildingSides: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.INT,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: () => this.isHeatingCalculatedByThermalEnvelope,
      formValidationRule: 'required',
      productDependency: undefined
    },
    isolation: {
      val: undefined,
      formProcessType: FormProcessType.ISOLATION,
      formRevertType: FormProcessType.ISOLATION,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    dhwEnabled: {
      val: undefined,
      formProcessType: FormProcessType.BOOLEAN,
      formRevertType: FormProcessType.BOOLEAN,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    dhwPersons: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.INT,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: false,
      formValidationRule: 'required|integer|min_value:1',
      productDependency: undefined
    },
    dhwDemand: {
      val: false,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.DECIMAL,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: false,
      formValidationRule: () => {
        return { oneOf: userStore.dhwValues, required: true }
      },
      productDependency: undefined
    },
    circuits: {
      val: undefined,
      formProcessType: FormProcessType.CIRCUIT,
      formRevertType: FormProcessType.CIRCUIT,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: true,
      formValidationRule: {
        flowTemperature: 'required',
        systemType: 'required'
      },
      productDependency: undefined
    },
    heatSource: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.ENERGY_SOURCE,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: true,
      formValidationRule: 'required',
      productDependency: undefined
    },
    groundType: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: false,
      formValidationRule: () => {
        return { oneOf: userStore.groundTypes?.map(g => g.name) || [], required: true }
      },
      productDependency: undefined
    },
    groundTypesEnabled: {
      val: undefined,
      formProcessType: FormProcessType.BOOLEAN,
      formRevertType: FormProcessType.BOOLEAN,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    heatPumpId: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.HEAT_PUMP_SYSTEM,
      resetsFromStep: HpStep.COMPONENTS,
      required: true,
      formValidationRule: undefined,
      productDependency: undefined
    },
    heatPumpCascadeSize: {
      val: undefined,
      formProcessType: FormProcessType.INT,
      formRevertType: FormProcessType.INT,
      step: HpStep.HEAT_PUMP_SYSTEM,
      resetsFromStep: HpStep.COMPONENTS,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    additionalProductId: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.HEAT_PUMP_SYSTEM,
      resetsFromStep: HpStep.COMPONENTS,
      required: () => !!projectStore.selectedHeatpump?.chr.additionalProducts?.length && !this.projectProperties.applianceInterfaceId.val,
      formValidationRule: undefined,
      productDependency: undefined
    },
    applianceInterfaceId: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.HEAT_PUMP_SYSTEM,
      resetsFromStep: HpStep.COMPONENTS,
      required: () => !!projectStore.selectedHeatpump?.chr.additionalProducts?.length && !this.projectProperties.additionalProductId.val,
      formValidationRule: undefined,
      productDependency: undefined
    },
    salePackage: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.HEAT_PUMP_SYSTEM,
      resetsFromStep: HpStep.SALE_SET,
      formValidationRule: undefined,
      required: false,
      productDependency: undefined
    },
    salePackageId: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.SALE_SET,
      resetsFromStep: HpStep.COMPONENTS,
      required: () => !(userStore.enableBundleSelection && this._projectProperties.salePackage.val === SalesPackageType.BUNDLE),
      formValidationRule: undefined,
      productDependency: undefined
    },
    dhwStorageId: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.COMPONENTS,
      resetsFromStep: HpStep.INSTALLATION,
      required: () => this.requiresDhwStorage,
      formValidationRule: undefined,
      productDependency: ProductType.DHWTANK
    },
    bufferTankId: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.COMPONENTS,
      resetsFromStep: HpStep.INSTALLATION,
      required: () => {
        if (projectStore.isHeating) {
          const bufferTankOptional = (!this._projectProperties.explicitlyNoBufferTankSelected.val && userStore.bufferTankOptional) || !userStore.bufferTankOptional
          const bufferTankSelected = this.isBufferTankSelected()

          if (!projectStore.bufferTankSuggestion?.heating.length && bufferTankOptional) {
            return true
          }

          return bufferTankOptional && !bufferTankSelected
        }

        return false
      },
      formValidationRule: undefined,
      productDependency: undefined
    },
    coolingBufferTankId: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.COMPONENTS,
      resetsFromStep: HpStep.INSTALLATION,
      required: () => {
        if (projectStore.isCooling && !projectStore.isHeating) {
          const bufferTankOptional = (!this._projectProperties.explicitlyNoBufferTankSelected.val && userStore.bufferTankOptional) || !userStore.bufferTankOptional
          const bufferTankSelected = this.isCoolingBufferTankSelected()

          if (!projectStore.bufferTankSuggestion?.cooling.length && bufferTankOptional) {
            return true
          }

          return bufferTankOptional && !bufferTankSelected
        }

        return false
      },
      formValidationRule: undefined,
      productDependency: undefined
    },
    explicitlyNoBufferTankSelected: {
      val: undefined,
      formProcessType: FormProcessType.BOOLEAN,
      formRevertType: FormProcessType.BOOLEAN,
      step: HpStep.COMPONENTS,
      resetsFromStep: HpStep.INSTALLATION,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    explicitlyNoBackupHeaterSelected: {
      val: undefined,
      formProcessType: FormProcessType.BOOLEAN,
      formRevertType: FormProcessType.BOOLEAN,
      step: HpStep.COMPONENTS,
      resetsFromStep: HpStep.INSTALLATION,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    explicitlyNoAccessoriesSelected: {
      val: undefined,
      formProcessType: FormProcessType.BOOLEAN,
      formRevertType: FormProcessType.BOOLEAN,
      step: HpStep.ACCESSORIES,
      resetsFromStep: HpStep.SYSTEM_SCHEMES,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    missingAccessoriesConfirmed: {
      val: false,
      formProcessType: FormProcessType.BOOLEAN,
      formRevertType: FormProcessType.BOOLEAN,
      step: HpStep.ACCESSORIES,
      resetsFromStep: HpStep.SYSTEM_SCHEMES,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    backupHeaterId: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.COMPONENTS,
      resetsFromStep: HpStep.INSTALLATION,
      required: false,
      formValidationRule: undefined,
      productDependency: ProductType.BACKUPHEATER
    },
    powerOutputIntegratedBackupHeater: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.DECIMAL,
      step: HpStep.COMPONENTS,
      resetsFromStep: HpStep.INSTALLATION,
      required: false,
      formValidationRule: undefined,
      productDependency: ProductType.BACKUPHEATER
    },
    systemSchemeId: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.SYSTEM_SCHEMES,
      resetsFromStep: HpStep.SUMMARY,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    systemSchemeDocumentNumber: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.SYSTEM_SCHEMES,
      resetsFromStep: HpStep.SUMMARY,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    selectedAccessories: {
      val: undefined,
      formProcessType: FormProcessType.ACCESSORY,
      formRevertType: FormProcessType.ACCESSORY,
      step: HpStep.ACCESSORIES,
      resetsFromStep: HpStep.SYSTEM_SCHEMES,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    systemControllerId: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.SYSTEM_CONTROLLERS,
      resetsFromStep: HpStep.ACCESSORIES,
      required: true,
      formValidationRule: undefined,
      productDependency: undefined
    },
    internetGatewayId: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.SYSTEM_CONTROLLERS,
      resetsFromStep: HpStep.ACCESSORIES,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    remoteControl: {
      val: undefined,
      formProcessType: FormProcessType.OBJECT,
      formRevertType: FormProcessType.OBJECT,
      step: HpStep.SYSTEM_CONTROLLERS,
      resetsFromStep: HpStep.ACCESSORIES,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    controllerExtensions: {
      val: undefined,
      formProcessType: FormProcessType.OBJECT,
      formRevertType: FormProcessType.OBJECT,
      step: HpStep.SYSTEM_CONTROLLERS,
      resetsFromStep: HpStep.ACCESSORIES,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    customArticles: {
      val: undefined,
      formProcessType: FormProcessType.OBJECT,
      formRevertType: FormProcessType.OBJECT,
      step: HpStep.ACCESSORIES,
      resetsFromStep: HpStep.EMPTY,
      required: false,
      formValidationRule: undefined,
      productDependency: undefined
    },
    customPrices: {
      val: undefined,
      formProcessType: FormProcessType.OBJECT,
      formRevertType: FormProcessType.OBJECT,
      step: HpStep.SUMMARY,
      resetsFromStep: HpStep.EMPTY,
      required: false,
      formValidationRule: ValidationRuleType.POSITIVE_ZERO_DECIMAL,
      productDependency: undefined
    },
    heatPumpPlacement: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      formValidationRule: undefined,
      required: false,
      productDependency: undefined
    },
    installationTypes: {
      val: [InstallationType.HEATING],
      formProcessType: FormProcessType.ARRAY,
      formRevertType: FormProcessType.ARRAY,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      formValidationRule: 'required',
      required: true,
      productDependency: undefined
    },
    hybrid: {
      val: false,
      formProcessType: FormProcessType.BOOLEAN,
      formRevertType: FormProcessType.BOOLEAN,
      step: HpStep.PLANNING,
      resetsFromStep: HpStep.HEAT_PUMP_SYSTEM,
      required: true,
      formValidationRule: undefined,
      productDependency: undefined
    },
    existingBackupHeater: {
      val: undefined,
      formProcessType: FormProcessType.KEY_NUM,
      formRevertType: FormProcessType.KEY_NUM,
      step: HpStep.COMPONENTS,
      resetsFromStep: HpStep.INSTALLATION,
      required: false,
      formValidationRule: {
        name: 'required|max:120',
        powerOutput: 'positiveDecimal'
      },
      productDependency: undefined
    },
    useExistingDhwTank: {
      val: undefined,
      formProcessType: FormProcessType.BOOLEAN,
      formRevertType: FormProcessType.BOOLEAN,
      step: HpStep.COMPONENTS,
      resetsFromStep: HpStep.INSTALLATION,
      required: false,
      formValidationRule: undefined,
      productDependency: ProductType.DHWTANK
    },
    existingDhwTank: {
      val: undefined,
      formProcessType: FormProcessType.KEY_NUM,
      formRevertType: FormProcessType.KEY_NUM,
      step: HpStep.COMPONENTS,
      resetsFromStep: HpStep.INSTALLATION,
      required: () => this.requiresExistingDhwTank,
      formValidationRule: {
        name: 'required|max:120',
        volume: 'positiveDecimal'
      },
      productDependency: undefined
    },
    electricityPrice: {
      val: undefined,
      formProcessType: FormProcessType.INTL_NUMBER,
      formRevertType: FormProcessType.INTL_NUMBER,
      step: HpStep.SUMMARY,
      resetsFromStep: HpStep.EMPTY,
      formValidationRule: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL],
      required: false,
      productDependency: undefined
    },
    comparisonFuelTypes: {
      val: undefined,
      formProcessType: FormProcessType.ARRAY,
      formRevertType: FormProcessType.ARRAY,
      step: HpStep.SUMMARY,
      resetsFromStep: HpStep.EMPTY,
      required: false,
      formValidationRule: {
        fuelType: 'required',
        price: 'required|positiveDecimal'
      },
      productDependency: undefined
    },
    heatExchanger: {
      val: false,
      formProcessType: FormProcessType.BOOLEAN,
      formRevertType: FormProcessType.BOOLEAN,
      step: HpStep.INSTALLATION,
      resetsFromStep: HpStep.SYSTEM_CONTROLLERS,
      formValidationRule: undefined,
      required: false,
      productDependency: undefined
    },
    hybridKit: {
      val: false,
      formProcessType: FormProcessType.BOOLEAN,
      formRevertType: FormProcessType.BOOLEAN,
      step: HpStep.INSTALLATION,
      resetsFromStep: HpStep.SYSTEM_CONTROLLERS,
      formValidationRule: undefined,
      required: false,
      productDependency: undefined
    },
    bufferStrategy: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.INSTALLATION,
      resetsFromStep: HpStep.SYSTEM_CONTROLLERS,
      formValidationRule: undefined,
      required: false,
      productDependency: undefined
    },
    mountingMaterial: {
      val: undefined,
      formProcessType: FormProcessType.BASIC,
      formRevertType: FormProcessType.BASIC,
      step: HpStep.INSTALLATION,
      resetsFromStep: HpStep.SYSTEM_CONTROLLERS,
      formValidationRule: undefined,
      required: false,
      productDependency: undefined
    },
  }
  private _bufferTankSuggestion?: BufferTankSuggestion

  constructor() {
    bus.$onC(this.componentName, BusEvent.LANGUAGE_CHANGED, () => {
      this.languageChanged()
    })
  }

  private languageChanged(): void {
    this.resetProducts()
  }

  public propertyIsRequired(key: ProjectPropertyKeyType): boolean {
    return typeof this._projectProperties[key].required === 'function'
      ? (this._projectProperties[key].required as () => boolean)()
      : !!this._projectProperties[key].required
  }

  public formPropertyIsRequired(key: string) {
    return containsRuleRequired(this._projectProperties[key as ProjectPropertyKeyType].formValidationRule)
  }

  public getProductCharacteristic<T extends Characteristic>(
    product: ProductResponse | undefined,
    type: CharacteristicType
  ): T | undefined {
    return product ? (product.characteristics.find(chr => chr.type == type) as T) : undefined
  }

  public getAllCharacteristicTypes(prod: ProductResponse): CharacteristicType[] {
    return prod.characteristics.map(chr => chr.type as CharacteristicType)
  }

  public characteristicProductIsIncluded(type: CharacteristicType): boolean {
    const addProduct = this.getSelectedProduct(ProductType.ADDITIONAL_PRODUCT)
    if (!addProduct) {
      return false
    }
    return !!this.getProductCharacteristic(addProduct, type)
  }

  public isBufferTankSelected(): boolean {
    return this.characteristicProductIsIncluded(CharacteristicType.BUFFER_TANK)
  }

  public isCoolingBufferTankSelected(): boolean {
    return this.characteristicProductIsIncluded(CharacteristicType.COOLING_BUFFER_TANK)
  }

  public isDhwTankSelected(): boolean {
    return this.characteristicProductIsIncluded(CharacteristicType.DHW_STORAGE)
  }

  get selectedProducts(): { [p: string]: ProductResponse | undefined } {
    return this._selectedProducts
  }

  set selectedProducts(value: { [p: string]: ProductResponse | undefined }) {
    this._selectedProducts = value
  }

  get selectedArticles(): Article[] {
    return this._selectedArticles
  }

  set selectedArticles(value: Article[]) {
    this._selectedArticles = value
  }

  get status(): ProjectStatus | undefined {
    return this._status
  }

  set status(value: ProjectStatus | undefined) {
    this._status = value
  }

  get isHeating(): boolean {
    return ((this._projectProperties.installationTypes.val as string[]) || []).includes(InstallationType.HEATING)
  }

  get isCooling(): boolean {
    return ((this._projectProperties.installationTypes.val as string[]) || []).includes(InstallationType.COOLING)
  }

  get requiresDhwStorage() {
    const selectedBackupHeater = this.getSelectedProduct(ProductType.BACKUPHEATER)
    let combiBoilerSelected = false
    if (selectedBackupHeater) {
      combiBoilerSelected = (this.getProductCharacteristic(selectedBackupHeater, CharacteristicType.BACKUP_HEATER) as BackupHeaterCharacteristic)?.dhwCapable
    }

    return (
      !this.characteristicProductIsIncluded(CharacteristicType.DHW_STORAGE) &&
      !this._projectProperties.existingDhwTank.val &&
      !this._projectProperties.existingBackupHeater.val?.dhw &&
      !combiBoilerSelected
    )
  }

  get requiresExistingDhwTank() {
    return !!this._projectProperties.useExistingDhwTank.val
  }

  get isCoolingCalculated(): boolean {
    return this.isCooling && this._projectProperties.coolingLoad.val === undefined
  }

  get isHeatingCalculatedByThermalEnvelope(): boolean {
    return (
      this.isHeating &&
      this._projectProperties.heatingCalculationMethod.val === HeatingCalculationMethod.THERMAL_ENVELOPE
    )
  }

  get isHeatingCalculatedByConsumption(): boolean {
    return (
      this.isHeating && this._projectProperties.heatingCalculationMethod.val === HeatingCalculationMethod.CONSUMPTION
    )
  }

  get bufferTanks() {
    return this.getProducts(ProductType.BUFFERTANK).map(p => new Buffertank(p))
  }

  get heatPumps(): Heatpump[] {
    return this.getProducts(ProductType.HEATPUMP).map(p => new Heatpump(p))
  }

  get salePackages(): Article[] {
    return this.getProducts(ProductType.SALES_PACKAGE).map(p => new Article(p))
  }

  get dhwStorages(): DhwStorage[] {
    return this.getProducts(ProductType.DHWTANK).map(p => new DhwStorage(p))
  }

  get backupHeaters(): BackupHeater[] {
    return this.getProducts(ProductType.BACKUPHEATER).map(p => new BackupHeater(p))
  }

  get reportVariants(): ReportVariant[] {
    return this._reportVariants
  }

  set reportVariants(value: ReportVariant[]) {
    this._reportVariants = value
  }

  get showPrices(): boolean {
    return this._showPrices
  }

  set showPrices(value) {
    this._showPrices = value
  }

  get showPricesCustomer(): boolean {
    return this._showPricesCustomer
  }

  set showPricesCustomer(value) {
    this._showPricesCustomer = value
  }

  get showExtendedNoiseValues(): boolean {
    return this._showExtendedNoiseValues
  }

  set showExtendedNoiseValues(value) {
    this._showExtendedNoiseValues = value
  }

  get origin(): ProjectOrigin {
    return this._origin
  }

  set origin(value: ProjectOrigin) {
    this._origin = value
  }

  /**
   * get system schemes with enabled caching
   */
  public async getSystemSchemes(): Promise<SchemeResponse[]> {
    if (this._systemSchemes === undefined) {
      return []
    }

    if (!(this._systemSchemes || []).length) {
      await ProjectController.loadSystemSchemes()
    }

    return this._systemSchemes || []
  }

  get systemSchemes(): SchemeResponse[] | undefined {
    return this._systemSchemes
  }

  set systemSchemes(value: SchemeResponse[] | undefined) {
    this._systemSchemes = value
  }

  /**
   * Returns accessories with enabled caching
   * If no accessories loaded yet await the load accessories
   */
  public async getAccessories(): Promise<Accessory[]> {
    if (this._accessories == undefined) {
      return []
    } //If undefined -> no accessories available
    if (!(this._accessories || []).length) {
      await ProjectController.loadAccessories()
    }

    return this._accessories || []
  }

  get accessories(): Accessory[] | undefined {
    return this._accessories
  }

  set accessories(value: Accessory[] | undefined) {
    this._accessories = value
  }

  get resolvedAccessories(): ProductResponse[] | undefined {
    return this._resolvedAccessories
  }

  set resolvedAccessories(accessories: ProductResponse[] | undefined) {
    this._resolvedAccessories = accessories
  }

  get salesPackages(): Article[] | undefined {
    return this._salesPackages
  }

  set salesPackages(value: Article[]) {
    this._salesPackages = value
  }

  get totalPrice(): number | undefined {
    return this._totalPrice
  }

  set totalPrice(value: number | undefined) {
    this._totalPrice = value
  }

  public installationTypeName(type: InstallationType): string {
    return type === InstallationType.COOLING ? 'COOLING' : 'HEATING'
  }

  /**
   * Checks if products already loaded and cached to return them without loading
   * If not, loads the products
   */
  public getProducts(type: ProductType): ProductResponse[] {
    if (this._products[type] === undefined) {
      return []
    }
    if (!(this._products[type] || []).length) {
      ProjectController.loadProducts(type).then()
    }
    return this._products[type] || []
  }

  get bufferTankSuggestion(): BufferTankSuggestion | undefined {
    this.bufferTanks
    return this._bufferTankSuggestion
  }

  set bufferTankSuggestion(value) {
    this._bufferTankSuggestion = value
  }

  get products(): { [p: string]: ProductResponse[] | undefined } {
    return this._products
  }

  set products(value: { [p: string]: ProductResponse[] | undefined }) {
    this._products = value
  }

  get lastModified(): string {
    return this._lastModified
  }

  set lastModified(value: string) {
    this._lastModified = value
  }

  get projectId(): string {
    return this._projectId
  }

  set projectId(value: string) {
    this._projectId = value
  }

  get projectProperties(): ProjectPropertyType {
    return this._projectProperties
  }

  get salePackage(): SalesPackageType | undefined {
    return this._projectProperties.salePackage.val ?? undefined
  }

  public resetProducts(): void {
    this._products[ProductType.HEATPUMP] = []
    this._products[ProductType.SYSTEM_CONTROLLER] = []
    this._products[ProductType.BACKUPHEATER] = []
    this._products[ProductType.BUFFERTANK] = []
    this._products[ProductType.DHWTANK] = []
    this._products[ProductType.SALES_PACKAGE] = []
    this._accessories = []
    this._systemSchemes = []
  }

  public resetSelectedProducts(): void {
    const productTypes = [
      ProductType.ADDITIONAL_PRODUCT,
      ProductType.BACKUPHEATER,
      ProductType.BUFFERTANK,
      ProductType.COOLING_BUFFERTANK,
      ProductType.HEATPUMP,
      ProductType.SYSTEM_CONTROLLER,
      ProductType.DHWTANK,
      ProductType.SALES_PACKAGE,
    ]

    productTypes.forEach(type => (this._selectedProducts[type] = undefined))
  }

  /**
   * Reset project properties to initial values
   */
  public resetProject() {
    for (const propertyName of Object.keys(this._projectProperties)) {
      const prop = this._projectProperties[propertyName as ProjectPropertyKeyType]
      prop.val = undefined
    }

    this._projectId = ''
    this._lastModified = ''
    this._state = []
    this.phasedOutSteps = []
    this._reportVariants = [ReportVariant.INSTALLER, ReportVariant.CONSUMER]
    this._origin = ProjectOrigin.STANDALONE

    this.resetProducts()
    this.resetSelectedProducts()
  }

  public pushState(): void {
    const currState: Record<string, FormProcessInputType> = {}
    for (const propertyName of Object.keys(this._projectProperties)) {
      currState[propertyName] = this._projectProperties[propertyName as ProjectPropertyKeyType].val
    }
    this._state.push(currState)
  }

  public popState(): void {
    const lastState = this._state.pop()
    if (!lastState) {
      return
    }
    for (const propertyName of Object.keys(this._projectProperties)) {
      this._projectProperties[propertyName as ProjectPropertyKeyType].val = lastState[propertyName]
    }
  }

  public dropState(): void {
    this._state.pop()
  }

  /**
   * Check for phased out products and save the corresponding step
   * @return boolean true if active, false if outphased
   */
  public processProductStatus(product: ProductResponse, prop: ProjectPropertyValueType): boolean {
    return this.processProductStatusStep(product, prop.step)
  }

  public processProductStatusStep(product: ProductResponse, step: HpStep): boolean {
    if (product.status !== ProductStatus.PHASED_OUT) {
      return true
    }
    this.phasedOutSteps.push(step)
    return false
  }

  public stepContainsPhasedOutProduct(step: HpStep): boolean {
    return this.phasedOutSteps.includes(step)
  }

  // FIXME: Is still really unused?
  get filteredSelectedAccessories(): KeyNumType | undefined {
    const val = this._projectProperties.selectedAccessories.val as KeyNumType | undefined
    if (val === undefined || Object.keys(val).length === 0) {
      return undefined
    }
    const applianceInterfaceId = this._projectProperties.applianceInterfaceId.val
    if (!applianceInterfaceId) {
      return val
    }
    const keys = Object.keys(val)
    if (keys.length === 1 && keys[0] === applianceInterfaceId) {
      return undefined
    }
    return val
  }

  get preselectedAccessories(): KeyNumType {
    return this._preselectedAccessories
  }

  set preselectedAccessories(value: KeyNumType) {
    this._preselectedAccessories = value
  }

  get coolingLoad(): number {
    return (this.projectProperties.coolingLoad.val as number) || 0
  }

  hasMixedCircuit(): boolean {
    return new Set(this.projectProperties.circuits.val?.map(c => c.flowTemperature)).size > 1
  }

  get heatingFlowTemperature(): number | undefined {
    if (!projectStore.isHeating) {
      return
    }

    const circuits = this.projectProperties.circuits.val

    if (circuits) {
      const heatingFlowTemperatures = circuits.map(c => c.flowTemperature || 0)
      return Math.max(...heatingFlowTemperatures)
    }

    return undefined
  }

  public getSelectedProduct(type: ProductType): ProductResponse | undefined {
    return this._selectedProducts[type]
  }

  public get selectedHeatpump(): Heatpump | undefined {
    const hp = this._selectedProducts[ProductType.HEATPUMP]
    return hp ? new Heatpump(this._selectedProducts[ProductType.HEATPUMP]) : undefined
  }

  public getSelectedProductName(type: ProductType): string {
    return this.getSelectedProduct(type)?.name || 'none'
  }

  get errorLoadingProject(): boolean {
    return this._errorLoadingProject
  }

  public getHeatingPowerOutput(hp: HPCharacteristic): number | undefined {
    return hp.heating?.operatingPoint?.powerOutput
  }

  public getArticleByCharacteristic(characteristic: CharacteristicType): Article | undefined {
    return this.selectedArticles.find(a => a.details?.getProductCharacteristic(characteristic))
  }

  public getArticlesByCharacteristic(...characteristic: CharacteristicType[]): Article[] {
    return this.selectedArticles.filter(a => characteristic.some(c => a.details?.getProductCharacteristic(c)))
  }
}

const projectStore = reactive(new ProjectStore())
export default projectStore
